<template>
  <Modal v-model="isShowModal" :isFeedback="true" :title="$t('feedbackForm.label')" :buttons="{ cancel: false, primary: false }" class="feedback-modal-wide">
    <div v-if="backendIsAlive" class="h-full flex flex-col">
      <div ref="chatMessages" class="feedback_chat_area grow bg-gray-50 px-1 overflow-auto">
        <ChatMessage v-for="item in chat" :key="item.id" :name="item.name" :avatar="item.avatar" :text="item.text" :sent="item.sent" :bgColor="item.bgColor" />
      </div>

      <div class="flex-none">
        <div class="flex flex-col gap-1 bg-gray-50 px-1 pb-1">
          <template v-if="showKeyboard">
            <button
              v-for="category in categoryOptions(step.cat || undefined)"
              :key="category.id"
              class="w-full text-left px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-blue-400 transition-colors text-sm"
              @click.stop="postUserMessage(Object.assign({ text: $t(`feedbackForm.chatAnswers.question${step.n}.${category.value}`) }, category))"
            >
              {{ $t(`feedbackForm.chatAnswers.question${step.n}.${category.value}`) }}
            </button>
          </template>
          <button
            v-if="step.n > 1"
            class="w-full text-left px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-blue-400 transition-colors text-sm"
            @click.stop="stepBack"
          >
            ↩
          </button>
        </div>
      </div>

      <ChatInputArea class="flex-none" :placeholder="$t('feedbackForm.enterYourMessage')" v-model="form.message" v-model:files="files" @send="text => postUserMessage({ text })" :disabled="!canSendFeedback" :enableFiles="!!showForm" v-model:sending="sending">
        <template v-if="showForm" #input>
          <form ref="feedbackform" class="feedbackform flex flex-col justify-between w-full bg-gray-50 p-1">
            <div class="flex flex-col space-y-1">
              <input
                v-if="!form.name || !isUserAuthenticated"
                class="input text-sm font-medium p-3 w-full border border-gray-300"
                type="text"
                :placeholder="$t('feedbackForm.fio')"
                :required="!isUserAuthenticated"
                v-model="form.name"
              />

              <input
                type="text"
                class="input text-sm font-medium p-3 w-full border border-gray-300"
                :placeholder="$t('feedbackForm.email')"
                :required="!isUserAuthenticated"
                v-model="form.email"
              />

              <input
                v-if="!isInCourse"
                type="text"
                class="input text-sm font-medium p-3 w-full border border-gray-300"
                :placeholder="$t('feedbackForm.course')"
                :required="form.mailTag === 'course'"
                v-model="form.courseName"
              />

              <textarea
                class="input rounded-none text-sm font-medium p-3 w-full border border-gray-300 min-h-[100px]"
                :placeholder="form.mailTag === 'tech' ? $t('feedbackForm.techDescription') : $t('feedbackForm.description')"
                required
                v-model="form.message"
              />
            </div>
          </form>
        </template>
      </ChatInputArea>
    </div>
    <div v-else>
      <p>{{ $t('feedbackForm.status.error.notWorking') }}</p>
      <p>{{ $t('feedbackForm.sendMessage') }}
        <btn-wrapper type="a"
                     @click.prevent="copyEmail" :label="feedbackEmail"/>
      </p>
    </div>
  </Modal>
</template>

<script>
import { defineComponent } from 'vue'
import Modal from "@/components/UI/modal/index.vue"
import {checkSentry} from "@/script"
import ChatMessage from './components/ChatMessage.vue'
import ChatInputArea from './components/ChatInputArea.vue'
import {useToast} from "vue-toastification"
import * as Sentry from "@sentry/vue"
import { mapState, mapActions } from 'vuex'

const toast = useToast()

export default defineComponent({
  name: 'FeedbackFormNew',

  components: { Modal, ChatMessage, ChatInputArea },

  data () {
    return {
      chat: [],
      chatUsers: {
        admin: { uid: 'admin', name: 'Админ', avatar: 'ТП', role: 'admin' },
      },
      step: { n: 1, cat: null },
      sending: false,
      isShowModal: false,
      showKeyboard: true,
      categories: [
        { value: 'access', level: 1 },
        { value: 'learning', level: 1 },
        { value: 'certificate', finalTag: 'course', level: 2 },
        { value: 'technical', level: 1 },
        { value: 'deanery', level: 1 },
        { value: 'improvement', level: 1 },

        { value: 'access.no_access', finalTag: 'study', parent: 'access', level: 2 },
        { value: 'access.application', finalTag: 'course', parent: 'access', level: 2 },
        { value: 'access.form', finalTag: 'tech', parent: 'access' },

        { value: 'learning.content', finalTag: 'course', parent: 'learning', level: 2 },
        { value: 'learning.exams', finalTag: 'course', parent: 'learning', level: 2 },
        { value: 'learning.help', finalTag: 'course', parent: 'learning', level: 2 },
        { value: 'learning.certificate', finalTag: 'course', parent: 'learning', level: 2 },

        { value: 'technical.access', finalTag: 'tech', parent: 'technical', level: 2 },
        { value: 'technical.element', finalTag: 'tech', parent: 'technical', level: 2 },
        { value: 'technical.site', finalTag: 'tech', parent: 'technical', level: 2 },

        { value: 'deanery.process', finalTag: 'study', parent: 'deanery', level: 2 },
        { value: 'deanery.documents', finalTag: 'study', parent: 'deanery', level: 2 },
        { value: 'deanery.other', finalTag: 'study', parent: 'deanery', level: 2 },

        { value: 'improvement.course', finalTag: 'course', parent: 'improvement', level: 2 },
        { value: 'improvement.platform', finalTag: 'tech', parent: 'improvement', level: 2 },
        { value: 'improvement.mobile', finalTag: 'tech', parent: 'improvement', level: 2 }
      ],
      adminReactions: [
        { id: 'start', text: 'feedbackForm.chat.question1' },
        { id: 'access', text: 'feedbackForm.chat.question2A' },
        { id: 'learning', text: 'feedbackForm.chat.question2B' },
        { id: 'technical', text: 'feedbackForm.chat.question2C' },
        { id: 'deanery', text: 'feedbackForm.chat.question2D' },
        { id: 'improvement', text: 'feedbackForm.chat.question2E' },
        { id: 'course_warning', text: 'feedbackForm.status.error.course', bgColor: 'bg-red-200' },
        { id: '*', text: 'feedbackForm.chat.finalMessage', level: 3 }
      ],
      form: {
        name: '',
        email: '',
        message: '',
        courseName: '',
        mailTag: null,
        subject: ''
      },
      files: [],
      selectedCategoryValue: null
    }
  },

  computed: {
    ...mapState('feedback', {
      storedFormData: state => state.formData
    }),

    currentUser: vm => vm.$root?.user || {},
    categoryOptions: vm => parent => {
      return vm.categories.filter(el => el.parent === parent)
    },
    getCategory: vm => value => {
      value ??= vm.step.cat
      return vm.categories.find(el => el.value === value)
    },
    adminReaction: vm => answerId => {
      return vm.adminReactions.find(el => el.id === (answerId || undefined) || (el.id === '*' && el.level === vm.step.n))
    },
    selectedCategory: vm => vm.categories.find(el => el.id === vm.step.cat) || {},
    showForm: vm => {
      let result = vm.getCategory()?.finalTag
      if (result === 'course' && !vm.$route.params?.courseId) {
        result = false
      }
      return !!result
    },
    canSendFeedback: vm => {
      const result = vm.step.n === 3
      if (result) {
        return vm.validateForm()
      }
      return result
    },
    backendIsAlive() {
      return this.$store.getters.getBackendStatus === 'OK'
    },
    feedbackEmail() {
      return this.$store.getters.getCustomInformation.feedbackEmail
    },
    isInCourse() {
      return !!this.$route.params?.courseId
    },
    isUserAuthenticated() {
      return !!this.$root?.user?.id
    },
  },

  mounted () {
    // ** add current user to chat
    const user = { uid: 'guest', login: 'user', name: 'Гость', avatar: 'NO' }
    if (this.currentUser.id) {
      const fullname = this.currentUser.fullname.split(' ')
      user.uid = this.currentUser.id
      user.avatar = fullname[0].charAt(0) + fullname[1].charAt(0)
      user.name = `${fullname[0]} ${fullname[1].charAt(0)}.`
      if (fullname.length > 2) {
        user.name += ` ${fullname[2].charAt(0)}.`
      }
    }
    this.addChatUser(user)

    // ** show first admin message
    this.clearChat()
  },

  methods: {
    ...mapActions('feedback', [
      'saveFeedbackFormData',
      'clearFeedbackFormData'
    ]),

    addChatUser (user) {
      const login = user.login || user.uid
      this.chatUsers[login] = user
    },

    clearChat () {
      this.chat = []
      this.step = { n: 1, cat: null }
      this.form.mailTag = null
      this.selectedCategoryValue = null
      this.saveFeedbackFormData({
        step: this.step,
        selectedCategoryValue: this.selectedCategoryValue,
        mailTag: this.form.mailTag,
        subject: this.form.subject,
        courseName: this.form.courseName
      })
      this.postAdminMessage(this.adminReaction('start'))
    },

    postAdminMessage (message) {
      if (!message) return

      const translatedMessage = {
        ...message,
        text: this.$t(message.text)
      }
      setTimeout(() => this.postMessage(Object.assign({}, this.chatUsers.admin, translatedMessage)), 500);
    },

    postUserMessage (category) {
      if (this.step.n === 3 && this.$refs.feedbackform) {
        return this.feedbackSendEmail(new Event('custom')).then(() => {
          this.clearChat()
          this.clearFeedbackFormData()
        }).catch(err => {
          console.error('Error sending feedback:', err)
        })
      }
      const userMessage = Object.assign({ sent: true, bgColor: 'bg-blue-300', text: category.text, commandId: category.value }, this.chatUsers.user)
      this.postMessage(userMessage)
      this.answerUserMessage()
    },

    answerUserMessage (message) {
      message ??= this.chat[this.chat.length - 1]

      if (message.commandId) {
        const category = this.getCategory(message.commandId)
        if (category) {
          Object.assign(this.step, { n: category.level + 1, cat: category.value })
          this.selectedCategoryValue = category.value
          if (category.finalTag) {
            this.form.mailTag = category.finalTag
            this.generateSubject(category)
          }
          this.saveFeedbackFormData({
            step: this.step,
            selectedCategoryValue: this.selectedCategoryValue,
            mailTag: this.form.mailTag,
            subject: this.form.subject,
            courseName: this.form.courseName
          })
        }

        let ansId = category.value
        if (category.finalTag === 'course' && !this.form.courseName) {
          ansId = 'course_warning'
        }

        let ans = this.adminReaction(ansId)

        if (ans) {
          this.postAdminMessage(this.adminReaction(ans?.id || this.step.cat))
        }
      }
    },

    stepBack () {
      const curStep = this.getCategory()
      if (curStep) {
        this.step = curStep.parent
          ? { n: curStep.level, cat: curStep.parent }
          : { n: 1, cat: null }
        this.selectedCategoryValue = curStep.parent || null
        this.saveFeedbackFormData({
          step: this.step,
          selectedCategoryValue: this.selectedCategoryValue,
          mailTag: this.form.mailTag,
          subject: this.form.subject,
          courseName: this.form.courseName
        })
      }
    },

    postMessage (message) {
      if (typeof message === 'string') {
        message = { id: '123', text: message, name: this.form.name, avatar: 'US' }
      } else if (typeof message === 'object') {
        if (!message.uid) {
          message.uid = this.chat.length + 1
        }
      } else {
        return
      }

      this.chat.push(message)

      this.$refs.chatMessages && this.$nextTick(() => (this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight))
    },

    validateForm () {
      return (!!this.form.name && !!this.form.email && !!this.form.message)
    },

    async feedbackSendEmail(evt) {
      evt.preventDefault()

      if (!this.form.mailTag && this.selectedCategoryValue) {
        const category = this.categories.find(cat => cat.value === this.selectedCategoryValue)
        if (category) {
          this.form.mailTag = category.finalTag
          this.generateSubject(category)
        }
      }

      if (!this.form.subject && this.form.mailTag && this.selectedCategoryValue) {
        const category = this.categories.find(cat => cat.value === this.selectedCategoryValue)
        if (category) {
          this.generateSubject(category)
        }
      }

      const formData = {
        mailTag: this.form.mailTag,
        subject: this.form.subject,
        name: this.form.name,
        email: this.form.email,
        message: this.form.message,
        courseName: this.form.courseName,
        files: this.files,
        elementCourse: this.$route.path,
        errors: this.$store.getters['errorlog'],
        userId: this.$root?.user?.id || ''
      }

      if (formData.mailTag === 'course') {
        formData.courseId = this.$route.params?.courseId
      }

      try {
        this.sending = true

        const optionTexts = {
          'course': this.$t('feedbackForm.question.aboutCourse'),
          'tech': this.$t('feedbackForm.question.technicalSupport'),
          'study': this.$t('feedbackForm.question.study')
        }

        const optionInfos = {
          'course': this.$t('feedbackForm.question.info.aboutCourse'),
          'tech': this.$t('feedbackForm.question.info.technicalSupport'),
          'study': this.$t('feedbackForm.question.info.study')
        }

        const option = {
          tag: formData.mailTag,
          value: optionTexts[formData.mailTag] || this.$t('feedbackForm.question.other'),
          info: optionInfos[formData.mailTag] || this.$t('feedbackForm.question.info.other')
        }

        const payload = {
          option: option,
          subject: formData.subject,
          name: formData.name,
          email: formData.email,
          message: formData.message,
          userId: formData.userId,
          courseName: formData.courseName,
          file: formData.files,
          elementCourse: formData.elementCourse,
          errors: formData.errors,
          mailTag: formData.mailTag,
          courseId: formData.courseId
        }


        await this.$store.dispatch('feedbackFormSendEmail', payload)
        toast.success(this.$t('feedbackForm.status.success'));

        this.form = {
          name: this.$root?.user?.fullname || '',
          email: this.$root?.user?.email || '',
          message: '',
          courseName: '',
          mailTag: null,
          subject: ''
        }
        this.files = []
        this.isShowModal = false

        this.clearFeedbackFormData()

        if (checkSentry()) {
          Sentry.setTag('character', {
            name: 'Feedback Form LXP',
            courseName: formData.courseName,
            title: formData.subject,
          })

          Sentry.captureFeedback({
            name: formData.name,
            email: formData.email,
            message: formData.message,
            tags: {
              courseName: formData.courseName,
              title: formData.subject,
              feedbackType: formData.mailTag
            }
          }, {
            includeReplay: true,
          })
        }

        return Promise.resolve()
      } catch (err) {
        console.log(err)
        toast.error(this.$t('feedbackForm.status.error.send'));
        return Promise.reject(err)
      } finally {
        this.sending = false
      }
    },

    generateSubject(category) {
      if (category) {
        if (!category.parent) {
          this.form.subject = this.$t(`feedbackForm.chatAnswers.question1.${category.value}`)
          this.saveFeedbackFormData({
            step: this.step,
            selectedCategoryValue: this.selectedCategoryValue,
            mailTag: this.form.mailTag,
            subject: this.form.subject,
            courseName: this.form.courseName
          })
          return
        }

        const mainCategory = this.categories.find(cat => cat.value === category.parent)
        if (mainCategory) {
          const mainCategoryText = this.$t(`feedbackForm.chatAnswers.question1.${mainCategory.value}`)
          const subCategoryText = this.$t(`feedbackForm.chatAnswers.question2.${mainCategory.value}.${category.value.split('.')[1]}`)
          this.form.subject = `${mainCategoryText}: ${subCategoryText}`
          this.saveFeedbackFormData({
            step: this.step,
            selectedCategoryValue: this.selectedCategoryValue,
            mailTag: this.form.mailTag,
            subject: this.form.subject,
            courseName: this.form.courseName
          })
        }
      }
    },

    copyEmail(event){
      navigator.clipboard.writeText(event.target.innerText)
        .then(() => toast.info(this.$t('footer.copyToBuffer')))
        .catch(err => console.error(this.$t('footer.errorCopyToBuffer')))
    },

    async getFeedbackInfo() {
      const courseId = this.$route?.params?.courseId

      if (checkSentry()) {
        try {
          const replay = Sentry.getReplay()
          await replay.flush()
        } catch (err) {
          console.warn(err.message)
        }
      }

      this.isShowModal = true
      this.form = {
        name: this.$root?.user?.fullname || '',
        email: this.$root?.user?.email || '',
        message: '',
        courseName: this.storedFormData?.courseName || '',
        mailTag: this.storedFormData?.mailTag || null,
        subject: this.storedFormData?.subject || ''
      }

      this.step = this.storedFormData?.step || { n: 1, cat: null }
      this.selectedCategoryValue = this.storedFormData?.selectedCategoryValue || null

      this.files = []

      if (!this.chat.length) {
        this.clearChat()
      } else {
        if (this.selectedCategoryValue) {
          const category = this.categories.find(cat => cat.value === this.selectedCategoryValue)
          if (category && category.finalTag) {
            this.form.mailTag = category.finalTag
            if (!this.form.subject) {
              this.generateSubject(category)
            }
          }
        }
      }

      if (courseId && !this.form.courseName) {
        this.getCourseName(courseId)
      }
    },

    async getCourseName(courseId) {
      if (!courseId) return
      try {
        const res = await this.$store.dispatch('getFullCourseInfo', { id: courseId })
        this.form.courseName = res.courses[0].fullname
        this.saveFeedbackFormData({
          step: this.step,
          selectedCategoryValue: this.selectedCategoryValue,
          mailTag: this.form.mailTag,
          subject: this.form.subject,
          courseName: this.form.courseName
        })
      } catch (err) {
        toast.error(err)
      }
    }
  }
})
</script>

<style lang="scss">
.feedback-modal-wide .modal__content {
  max-width: 800px !important;
  width: 90vw !important;
  /* width: 600px; */
  min-width: 380px;
  height: 800px;
}

.feedback_chat_area {
  min-width: 380px;
}

.feedbackform {
  .input:focus {
    outline: none !important;
    border:1px solid #719ECE;
  }
}
</style>